import React, { useState, useContext } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../services/firebase/firebaseConfig';
import { AuthContext } from '../Providers/AuthContext';
import { validateDOB } from '../../utils/ageUtils';
import styles from './AuthModal.module.css';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signIn' | 'signUp' | 'forgotPassword';
}

function AuthModal({ isOpen, onClose, initialMode = 'signIn' }: AuthModalProps) {
  const { accountType, linkWithGoogle, linkWithEmail } = useContext(AuthContext);
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signUp');
  const [isForgotPassword, setIsForgotPassword] = useState(initialMode === 'forgotPassword');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dobError, setDobError] = useState('');

  if (!isOpen) return null;

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setDobError('');
    setVerificationSent(false);

    try {
      if (isSignUp) {
        // Validate DOB for sign-up
        const dobValidation = validateDOB(dateOfBirth);
        if (!dobValidation.valid) {
          setDobError(dobValidation.error || 'Invalid date of birth');
          setLoading(false);
          return;
        }

        // If signing up from temp account, LINK the account (preserve progress)
        if (accountType === 'temp') {
          const result = await linkWithEmail(email, password, dateOfBirth);

          if (!result.success) {
            // If email is already in use, make it VERY clear what the user needs to do
            if (result.error?.includes('already registered') || result.error?.includes('already in use')) {
              setError(`${result.error} Click "Already have an account? Sign In" below to access your existing account.`);
            } else {
              setError(result.error || 'Failed to create account');
            }
            setLoading(false);
            return;
          }

          setVerificationSent(true);

          // Close modal after brief delay
          setTimeout(() => {
            onClose();
            window.location.reload();
          }, 2000);
        } else {
          // Creating brand new account (no temp account to preserve)
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);

          // Send email verification
          try {
            await sendEmailVerification(userCredential.user);
            setVerificationSent(true);
          } catch (verifyError: any) {
            // Don't fail the signup if verification email fails to send
          }

          // Close modal after a brief delay to show verification message
          setTimeout(() => {
            onClose();
            window.location.reload();
          }, 3000);
        }
      } else {
        // Signing in to EXISTING account
        // If we're in a temp account, sign out first to avoid conflicts
        if (accountType === 'temp' && auth.currentUser) {
          await auth.signOut();
        }

        // Sign in to existing account
        await signInWithEmailAndPassword(auth, email, password);

        // Clear temp account data from localStorage (discarding temp progress)
        if (accountType === 'temp') {
          localStorage.removeItem('zodiac_temp_account');
        }

        onClose();
        // Reload to update UI with real account
        window.location.reload();
      }
    } catch (err: any) {
      // Handle specific Firebase auth errors
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect email or password. Try "Forgot Password?" if you need to reset it.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();

      // If user has a temp account, try to sign in first (in case they already have an account)
      if (accountType === 'temp') {

        try {
          // Try normal sign-in first (this will work if they already have a Google account)
          await signInWithPopup(auth, provider);

          // Clear temp account data
          localStorage.removeItem('zodiac_temp_account');

          onClose();
          window.location.reload();
          return;
        } catch (signInError: any) {
          // If sign-in fails with popup-closed or cancelled, just return
          if (signInError.code === 'auth/popup-closed-by-user' ||
              signInError.code === 'auth/cancelled-popup-request') {
            setLoading(false);
            return;
          }

          // If the error is NOT "no account exists", then it's a real error
          if (signInError.code !== 'auth/account-exists-with-different-credential') {
            // Try to link the account instead
            const result = await linkWithGoogle();

            if (!result.success) {
              setError(result.error || 'Failed to link Google account');
              setLoading(false);
              return;
            }

            setTimeout(() => {
              onClose();
              window.location.reload();
            }, 500);
            return;
          } else {
            // Real error occurred
            throw signInError;
          }
        }
      } else {
        // No temp account - normal Google sign-in for real accounts
        await signInWithPopup(auth, provider);
        onClose();
        window.location.reload();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setResetEmailSent(false);

    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);

      // Close modal after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setError('');
    setVerificationSent(false);
    setResetEmailSent(false);
    setDateOfBirth('');
    setDobError('');
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    setIsForgotPassword(false);
    resetForm();
  };

  const switchToForgotPassword = () => {
    setIsForgotPassword(true);
    setIsSignUp(false);
    resetForm();
  };

  const switchToSignIn = () => {
    setIsForgotPassword(false);
    setIsSignUp(false);
    resetForm();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>

        <h2 className={styles.title}>
          {isForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Sign In'}
        </h2>

        {/* Mode indicator - helps users understand what will happen */}
        {!isForgotPassword && accountType === 'temp' && (
          <div className={`${styles.modeIndicator} ${isSignUp ? styles.signUp : styles.signIn}`}>
            {isSignUp ? (
              <>
                <span className={styles.modeIndicatorIcon}>🔒</span>
                Creating a real account will preserve your temp account progress
              </>
            ) : (
              <>
                <span className={styles.modeIndicatorIcon}>🔑</span>
                Signing in will replace your temp account with your existing account
              </>
            )}
          </div>
        )}

        <form onSubmit={isForgotPassword ? handlePasswordReset : handleEmailAuth} className={styles.form}>
          <div className={styles.inputGroup}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          {!isForgotPassword && (
            <div className={styles.inputGroup}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                required
              />
            </div>
          )}

          {isSignUp && !isForgotPassword && (
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Date of Birth</label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => {
                  setDateOfBirth(e.target.value);
                  setDobError('');
                }}
                className={styles.input}
                required
                max={new Date().toISOString().split('T')[0]}
              />
              {dobError && <span className={styles.fieldError}>{dobError}</span>}
              <span className={styles.inputHint}>
                Required for age-appropriate content settings
              </span>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}
          {verificationSent && (
            <div className={styles.success}>
              ✅ Account created! Verification email sent to {email}. Please check your inbox.
            </div>
          )}
          {resetEmailSent && (
            <div className={styles.success}>
              ✅ Password reset email sent to {email}. Please check your inbox.
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? 'Loading...' : (isForgotPassword ? 'Send Reset Email' : isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        {!isForgotPassword && (
          <>
            <div className={styles.divider}>
              <span>or</span>
            </div>

            <button
              onClick={handleGoogleAuth}
              className={styles.googleButton}
              disabled={loading}
            >
              🔑 Continue with Google
            </button>
          </>
        )}

        <div className={`${styles.switchMode} ${error && error.includes('already') && isSignUp ? styles.highlighted : ''}`}>
          {isForgotPassword ? (
            <>
              Remember your password?{' '}
              <button
                type="button"
                onClick={switchToSignIn}
                className={styles.switchButton}
              >
                Sign In
              </button>
            </>
          ) : (
            <>
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{' '}
              <button
                type="button"
                onClick={switchMode}
                className={styles.switchButton}
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </button>
            </>
          )}
        </div>

        {!isForgotPassword && !isSignUp && (
          <div className={styles.forgotPassword}>
            <button
              type="button"
              onClick={switchToForgotPassword}
              className={styles.forgotPasswordButton}
            >
              Forgot Password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthModal;